import React, { useEffect, useState } from "react";
import apiClient from "api/apiClient";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Alert,
  TextField,
  CardActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubscription } from "state/actions/subscriptionActions";

const subscriptions = [
  {
    key: "starter",
    name: "Starter",
    price: "CAD $700/month",
    features: [
      "20 credits per month",
      "Number of credits per simulation depends on the model and grid size",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    price: "CAD $1200/month",
    features: [
      "40 credits per month",
      "Number of credits per simulation depends on the model and grid size",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "CAD $2000/month",
    features: [
      "80 credits per month",
      "Number of credits per simulation depends on the model and grid size",
    ],
  },
];

const creditPacks = [
  {
    key: "credits_5",
    name: "5 Credits",
    price: "CAD $250",
    features: ["5 credits"],
  },
  {
    key: "credits_20",
    name: "20 Credits",
    price: "CAD $900",
    features: ["20 credits (10% discount)"],
  },
  {
    key: "credits_50",
    name: "50 Credits",
    price: "CAD $2000",
    features: ["50 credits (20% discount)"],
  },
];

const PricingPage = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showTrialInput, setShowTrialInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [trialMessage, setTrialMessage] = useState(null);

  useEffect(() => {
    dispatch(fetchSubscription());
  }, [dispatch]);

  const { data, loaded } = useSelector((state) => state.subscription);
  const plan = data?.plan;
  const pendingPlan = data?.pendingPlan;

  const handleSubscribe = async (planKey) => {
    setMessage(null);
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-subscription`,
        { key: planKey },
      );

      if (response.data.success) {
        setMessage("Your subscription has been activated.");
        setMessageType("success");
        dispatch(fetchSubscription());
      } else if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.message) {
        setMessage(response.data.message);
        setMessageType("info");
      }
    } catch (err) {
      console.error(err);
      setMessage("Failed to start subscription.");
      setMessageType("error");
    }
  };

  const handleCancel = async () => {
    try {
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/cancel-subscription`,
        {},
      );
      setMessage(res.data.message);
      setMessageType("info");
      dispatch(fetchSubscription());
    } catch (err) {
      console.error("Cancel failed", err);
      setMessage("Failed to cancel subscription.");
      setMessageType("error");
    }
  };

  const handleBuyCredits = async (packKey) => {
    setMessage(null);
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-credit-purchase`,
        { packKey },
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.success) {
        setMessage(`${response.data.creditsAdded} credits has been added.`);
        setMessageType("success");
        dispatch(fetchSubscription());
      } else {
        setMessage("Something went wrong starting the payment.");
        setMessageType("error");
      }
    } catch (err) {
      console.error(err);
      setMessage("Failed to initiate credit purchase.");
      setMessageType("error");
    }
  };

  const handleStartFreeTrial = async (code) => {
    setTrialMessage(null);
    try {
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-free-trial`,
        { promoCode: code },
      );

      if (res.data.success) {
        setMessage("Free trial started!");
        setMessageType("success");
        dispatch(fetchSubscription());
      } else {
        setTrialMessage("Failed to activate trial.");
      }
    } catch (err) {
      setTrialMessage(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    }
  };

  const renderSection = (title, products, actionLabel, type) => (
    <>
      <Typography variant="h4" fontWeight="bold" mt={4} mb={2}>
        {title}
      </Typography>
      <Grid container spacing={3}>
        {products.map((item) => {
          const isSubscribedPlan = plan && item.key === plan;
          const isPendingPlan = pendingPlan && item.key === pendingPlan;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={item.name}
              sx={{ display: "flex" }}
            >
              <Card
                elevation={3}
                sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
              >
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography variant="h5" gutterBottom>
                    {item.name}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {item.price}
                  </Typography>
                  <Box component="ul" sx={{ flexGrow: 1, pl: 2 }}>
                    {item.features.map((feature, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{feature}</Typography>
                      </li>
                    ))}
                  </Box>
                  {isSubscribedPlan ? (
                    <>
                      {data.scheduledPlanCancelled ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Box>
                            <Typography sx={{ mb: 1 }}>
                              You’ve cancelled your <strong>{item.name}</strong>{" "}
                              subscription.
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              You can’t change your plan until the end of your
                              current billing period.
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              Subscription ends on:{" "}
                              <strong>
                                {new Date(data.expiresAt).toLocaleDateString()}
                              </strong>
                            </Typography>
                            <Typography>
                              Remaining simulations:{" "}
                              <strong>{data.planCredits}</strong>
                            </Typography>
                          </Box>
                        </Alert>
                      ) : data.cancelAtPeriodEnd ? (
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => handleSubscribe(item.key)} // resubscribe
                        >
                          Resubscribe
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => setConfirmCancel(true)}
                        >
                          Cancel Subscription
                        </Button>
                      )}

                      {!data.scheduledPlanCancelled && data?.expiresAt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {data.cancelAtPeriodEnd
                            ? `Subscription ends on: ${new Date(
                                data.expiresAt,
                              ).toLocaleDateString()}`
                            : data.pendingPlan
                              ? `Switches to ${
                                  data.pendingPlan
                                } plan on: ${new Date(
                                  data.expiresAt,
                                ).toLocaleDateString()}`
                              : `Renews on: ${new Date(
                                  data.expiresAt,
                                ).toLocaleDateString()}`}
                        </Typography>
                      )}

                      {data?.planCredits !== undefined &&
                        !data.scheduledPlanCancelled && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Remaining simulations: {data.planCredits}
                          </Typography>
                        )}
                    </>
                  ) : isPendingPlan ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Downgrade scheduled for{" "}
                      {data?.expiresAt &&
                        new Date(data.expiresAt).toLocaleDateString()}
                    </Typography>
                  ) : data.scheduledPlanCancelled && type === "subscription" ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You can’t subscribe to a new plan until your current one
                      expires on{" "}
                      <strong>
                        {data?.expiresAt &&
                          new Date(data.expiresAt).toLocaleDateString()}
                      </strong>
                      .
                    </Alert>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() =>
                        type === "subscription"
                          ? handleSubscribe(item.key)
                          : handleBuyCredits(item.key)
                      }
                    >
                      {actionLabel}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {type === "subscription" && plan === "free_trial" && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                backgroundColor: "#e0f7fa",
                color: "#006064",
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎁 Free Trial Active
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  You are currently using a free trial plan.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Trial expires on:{" "}
                  <strong>
                    {new Date(data.expiresAt).toLocaleDateString()}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  Remaining simulations: <strong>{data.planCredits}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {type === "subscription" && !plan && !pendingPlan && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                backgroundColor: "#1e1e1e",
                color: "white",
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎁 Start Free Trial
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter a promo code to unlock free trial access to the Starter
                  plan.
                </Typography>
                {showTrialInput && (
                  <>
                    <TextField
                      fullWidth
                      label="Promo Code"
                      variant="outlined"
                      size="small"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#fff",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ccc",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#fff",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "#fff",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#fff",
                        },
                        input: { color: "#fff" },
                      }}
                    />
                    {trialMessage && (
                      <Typography variant="body2" color="error" mt={1}>
                        {trialMessage}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
              <CardActions
                sx={{
                  px: 2,
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                {showTrialInput ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ flex: 1 }}
                      onClick={() => handleStartFreeTrial(promoCode)}
                      disabled={!promoCode}
                    >
                      Activate Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      sx={{ flex: 1 }}
                      onClick={() => {
                        setShowTrialInput(false);
                        setPromoCode("");
                        setTrialMessage("");
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => setShowTrialInput(true)}
                  >
                    Use Promo Code
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>
      {confirmCancel && type === "subscription" && (
        <Box mt={2}>
          <Alert
            severity="warning"
            sx={{ width: "100%" }}
            action={
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setConfirmCancel(false)}
                  sx={{
                    borderColor: "#888",
                    color: "#fff",
                    "&:hover": { borderColor: "#ccc" },
                  }}
                >
                  No, keep it
                </Button>
                <Button
                  color="error"
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setConfirmCancel(false);
                    handleCancel();
                  }}
                >
                  Yes, cancel
                </Button>
              </Box>
            }
          >
            {data.pendingPlan
              ? "By cancelling now, you won’t be able to subscribe to a new plan until the end of the current billing period."
              : "Are you sure you want to cancel your subscription?"}
          </Alert>
        </Box>
      )}
    </>
  );

  return (
    <Box p={embedded ? 0 : 4}>
      {message && (
        <Alert
          severity={messageType}
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setMessage(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      )}

      {!loaded ? (
        <Typography variant="h6">
          Loading your subscription details...
        </Typography>
      ) : (
        <>
          {renderSection(
            "Monthly Subscription Plans",
            subscriptions,
            "Subscribe",
            "subscription",
          )}

          <Divider sx={{ my: 6 }} />

          <Box mt={4} mb={2}>
            <Typography variant="h4" fontWeight="bold" mb={4}>
              Buy Simulation Credits
            </Typography>

            {data?.credits !== undefined && (
              <Card elevation={3} sx={{ maxWidth: 320 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Remaining Credits:
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="secondary"
                    >
                      {data.credits}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {renderSection("", creditPacks, "Buy Credits", "credits")}
        </>
      )}
    </Box>
  );
};

export default PricingPage;
